
import { createTheme } from '@mui/material/styles';

const baseColors = {
  primary: {
    main: '#A90A0F',       
    light: '#FF7A5C',      
    dark: '#C54428',        
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F8EEDB',        
    light: '#FFFAF0',     
    dark: '#E6D4B7',        
    contrastText: '#333333',
  },
  background: {
    default: '#F8EEDB',     
    paper: '#FFFFFF',
  },
  text: {
    primary: '#2C3E50',     
    secondary: '#5D6D7E',   
  },
  grey: {
    50: '#F8F9FA',
    100: '#E9ECEF',
    200: '#DEE2E6',
    300: '#CED4DA',
    400: '#ADB5BD',
    500: '#6C757D',
    600: '#495057',
    700: '#343A40',
    800: '#212529',
    900: '#0D1117',
  },
  success: {
    main: '#27AE60',
    light: '#2ECC71',
    dark: '#229954',
  },
  warning: {
    main: '#F39C12',
    light: '#F4D03F',
    dark: '#D68910',
  },
  error: {
    main: '#E74C3C',
    light: '#EC7063',
    dark: '#C0392B',
  },
  info: {
    main: '#3498DB',
    light: '#5DADE2',
    dark: '#2980B9',
  },
};

const typography = {
  fontFamily: '"Inter", "Roboto", "Segoe UI", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.75rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2.25rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.875rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.005em',
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: '#5D6D7E',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: '#6C757D',
  },
};

const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        padding: '8px 24px',
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-1px)',
        },
      },
      contained: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        },
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
        },
      },
    
      text: {
        '&.navbar-link': {
          color: 'black',
          fontWeight: 700,
          fontSize: '13px',
          padding: '8px 12px',
          borderRadius: '8px',
          '&:hover': {
            color: '#EB5A3C', 
            backgroundColor: '#FFF6E9',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
      elevation3: {
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        boxShadow: '0 2px 12px rgba(235, 90, 60, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid rgba(0,0,0,0.05)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 32px rgba(235, 90, 60, 0.12)',
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        color: baseColors.text.primary,
        boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#FFFFFF',
        color: baseColors.text.primary,
        borderRight: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          backgroundColor: '#FFFAF0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#F8EEDB',
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        fontWeight: 500,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: '#F8F9FA',
        '& .MuiTableCell-head': {
          fontWeight: 600,
          color: baseColors.text.primary,
          borderBottom: '2px solid #E9ECEF',
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: '#F8F9FA',
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(0,0,0,0.08)',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(235, 90, 60, 0.08)', 
        },
        
        '&.navbar-icon': {
          color: 'gray',
          '&:hover': {
            color: '#EB5A3C',
            backgroundColor: '#FFF6E9',
          },
        },
        '&.social-icon': {
          padding: '8px',
          '&:hover': {
            transform: 'scale(1.1)',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '16px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(0,0,0,0.08)',
      },
      indicator: {
        height: '3px',
        borderRadius: '3px',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        minHeight: '48px',
        '&.Mui-selected': {
          fontWeight: 600,
        },
      },
    },
  },
 
  MuiListItem: {
    styleOverrides: {
      root: {
        '&.drawer-item': {
          margin: '4px 16px',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: '#FFF6E9',
            '& .MuiListItemText-primary': {
              color: '#EB5A3C', 
              fontWeight: 'bold',
            },
          },
        },
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'black',
      },
    },
  },
};

const theme = createTheme({
  palette: {
    mode: 'light',
    ...baseColors,
  },
  typography,
  components,
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.05)',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.1)',
    '0 8px 24px rgba(0,0,0,0.12)',
    '0 12px 32px rgba(0,0,0,0.15)',
    '0 16px 48px rgba(0,0,0,0.18)',
    '0 24px 64px rgba(0,0,0,0.2)',
    '0 32px 80px rgba(0,0,0,0.22)',
    '0 40px 96px rgba(0,0,0,0.25)',
    '0 48px 112px rgba(0,0,0,0.28)',
    '0 56px 128px rgba(0,0,0,0.3)',
    '0 64px 144px rgba(0,0,0,0.32)',
    '0 72px 160px rgba(0,0,0,0.35)',
    '0 80px 176px rgba(0,0,0,0.38)',
    '0 88px 192px rgba(0,0,0,0.4)',
    '0 96px 208px rgba(0,0,0,0.42)',
    '0 104px 224px rgba(0,0,0,0.45)',
    '0 112px 240px rgba(0,0,0,0.48)',
    '0 120px 256px rgba(0,0,0,0.5)',
    '0 128px 272px rgba(0,0,0,0.52)',
    '0 136px 288px rgba(0,0,0,0.55)',
    '0 144px 304px rgba(0,0,0,0.58)',
    '0 152px 320px rgba(0,0,0,0.6)',
    '0 160px 336px rgba(0,0,0,0.62)',
  ],
});

export default theme;